// ==========================================================================
// Pre-Alert UI - Promotional CTA card + onboarding-style modal
//
// Surfaces PreAlert-level preparedness actions when no weather alert is
// active. Card replaces the small banner; tapping "Explore How" opens a
// modal whose interaction model mirrors the onboarding flow (list view →
// slide right to per-action detail → "Start this action").
// ==========================================================================

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { carouselBackground, preActionBanner } from '@/assets';
import { track } from '@/lib/mixpanel';
import type { PreAlertAction } from '@/services/preAlertAction.service';

/**
 * Lower-case the first character of an action name so the locale-aware
 * "How to ..." template (en) reads naturally. Acronyms stay untouched —
 * if the second character is also upper-case, the word is treated as an
 * acronym ("WASH your hands" stays "WASH your hands").
 */
function lowercaseFirst(name: string): string {
  if (!name) return '';
  if (
    name.length > 1
    && name[1] === name[1].toUpperCase()
    && name[1] !== name[1].toLowerCase()
  ) {
    return name;
  }
  const first = name.charAt(0);
  if (first === first.toUpperCase() && first !== first.toLowerCase()) {
    return first.toLowerCase() + name.slice(1);
  }
  return name;
}

/**
 * Persistent CTA card shown below the forecast on the home screen.
 * Compact umbrella + title + "Explore How" pill, per spec.
 */
export function PreAlertCard({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();

  const handleClick = () => {
    track('FE Pre-Alert Card Tapped');
    onClick();
  };

  return (
    <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      <img
        src={preActionBanner}
        alt=""
        className="w-full h-[160px] object-cover"
        aria-hidden
      />
      <div className="px-4 py-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[15px] font-bold text-gray-900 leading-snug mb-1">
            {t('preAlert.cardTitle')}
          </h3>
          <p className="text-[12px] text-gray-500 leading-snug">
            {t('preAlert.cardBody')}
          </p>
        </div>
        <button
          type="button"
          onClick={handleClick}
          className="text-[13px] font-semibold px-4 py-2 rounded-full text-white shrink-0 active:opacity-80"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {t('preAlert.exploreHow')}
        </button>
      </div>
    </div>
  );
}

/**
 * Modal mirroring the onboarding modal layout — bottom-aligned card with
 * slide animations between list and per-action detail views.
 */
interface PreAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  actions: PreAlertAction[];
  onActivate: (actionId: number) => Promise<void> | void;
  isActivating: boolean;
}

type ModalView = 'list' | 'detail';

export function PreAlertModal({
  isOpen,
  onClose,
  actions,
  onActivate,
  isActivating,
}: PreAlertModalProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<ModalView>('list');
  const [selectedAction, setSelectedAction] = useState<PreAlertAction | null>(null);
  const [direction, setDirection] = useState<'right' | 'left'>('right');
  const [animKey, setAnimKey] = useState(0);

  // Lock body scroll while modal is visible
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  const openDetail = useCallback((action: PreAlertAction) => {
    track('FE Pre-Alert Action Tapped', {
      action_id: action.id,
      action_title: action.name,
    });
    setSelectedAction(action);
    setDirection('right');
    setAnimKey((k) => k + 1);
    setView('detail');
  }, []);

  const goBackToList = useCallback(() => {
    setDirection('left');
    setAnimKey((k) => k + 1);
    setView('list');
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    // Defer reset so the closing animation doesn't show a flash of the list
    setTimeout(() => {
      setView('list');
      setSelectedAction(null);
    }, 200);
  }, [onClose]);

  const handleStart = useCallback(async () => {
    if (!selectedAction) return;
    await onActivate(selectedAction.id);
  }, [selectedAction, onActivate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      <button
        type="button"
        aria-label={t('common.cancel')}
        onClick={handleClose}
        className="absolute inset-0 bg-black/30"
      />

      <div className="absolute inset-0 flex items-end justify-center p-4 pb-5 pointer-events-none">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden h-[75vh] pointer-events-auto">
          <div
            key={animKey}
            className={`h-full ${
              animKey > 0
                ? direction === 'right'
                  ? 'onboarding-slide-right'
                  : 'onboarding-slide-left'
                : 'fade-in'
            }`}
          >
            {view === 'list' ? (
              <ListView
                actions={actions}
                onClose={handleClose}
                onSelect={openDetail}
              />
            ) : selectedAction ? (
              <DetailView
                action={selectedAction}
                onBack={goBackToList}
                onClose={handleClose}
                onStart={handleStart}
                isStarting={isActivating}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Subviews
// ============================================================================

function ListView({
  actions,
  onClose,
  onSelect,
}: {
  actions: PreAlertAction[];
  onClose: () => void;
  onSelect: (action: PreAlertAction) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3 shrink-0">
        <h2 className="text-[20px] font-bold text-gray-900 leading-tight">
          {t('preAlert.modalTitle')}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.cancel')}
          className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div className="px-6 pb-6 overflow-y-auto flex-1">
        <div className="flex flex-col gap-2.5">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => onSelect(action)}
              className="w-full flex items-center justify-between gap-3 p-4 text-left rounded-xl bg-gray-100 active:bg-gray-200"
            >
              <span className="text-[15px] text-gray-800 leading-snug line-clamp-2">
                {t('preAlert.howTo', { name: lowercaseFirst(action.name) })}
              </span>
              <ArrowRight className="w-5 h-5 text-gray-700 shrink-0" strokeWidth={2.5} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function DetailView({
  action,
  onBack,
  onClose,
  onStart,
  isStarting,
}: {
  action: PreAlertAction;
  onBack: () => void;
  onClose: () => void;
  onStart: () => void;
  isStarting: boolean;
}) {
  const { t } = useTranslation();
  const sortedSteps = [...(action.steps ?? [])].sort((a, b) => a.order - b.order);
  // If the user already has a non-expired pre-alert UserAction for this
  // action, the BE attaches `user_action`. We swap the CTA to "Continue
  // Action" so the user understands tapping won't create a duplicate.
  const alreadyStarted =
    action.user_action?.status === 'active' || action.user_action?.status === 'assigned';

  return (
    <div className="flex flex-col h-full">
      <div className="relative h-[160px] overflow-hidden shrink-0">
        <img
          src={carouselBackground}
          alt=""
          className="w-full h-full object-cover"
          aria-hidden
        />
        <button
          type="button"
          onClick={onBack}
          aria-label={t('preAlert.back')}
          className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center"
        >
          <ArrowLeft className="w-4 h-4 text-gray-700" />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.cancel')}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pt-5 pb-6">
        <h2 className="text-[20px] font-bold text-gray-900 leading-tight mb-3">
          {action.name}
        </h2>

        {action.description && (
          <p className="text-[13px] text-gray-600 leading-relaxed mb-5">
            {action.description}
          </p>
        )}

        {sortedSteps.length > 0 && (
          <div className="flex flex-col gap-2 mb-5">
            {sortedSteps.map((step) => (
              <div key={step.id} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <span className="w-6 h-6 rounded-full bg-white border border-gray-200 text-gray-700 text-[11px] font-semibold flex items-center justify-center shrink-0">
                  {step.order}
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-gray-900 mb-0.5">{step.title}</p>
                  {step.description && (
                    <p className="text-[12px] text-gray-500 leading-snug">{step.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={onStart}
          disabled={isStarting}
          className="w-full h-[48px] rounded-xl text-white text-[15px] font-semibold disabled:opacity-60"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {isStarting
            ? t('prepare.starting')
            : alreadyStarted
              ? t('prepare.continueAction')
              : t('prepare.startAction')}
        </button>
      </div>
    </div>
  );
}
